import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ROUTES } from "../../../constants/routes";
import styles from "./Sidebar.module.css";

import { ReactComponent as TruckIcon } from "../../../assets/icons/Truck.svg";
import { ReactComponent as DashboardIcon } from "../../../assets/icons/Dashboard.svg";
import { ReactComponent as MapIcon } from "../../../assets/icons/Map.svg";
import { ReactComponent as PlanningIcon } from "../../../assets/icons/Planning.svg";
import { ReactComponent as HandbookIcon } from "../../../assets/icons/Handbook.svg";
import { ReactComponent as ReportIcon } from "../../../assets/icons/Report.svg";
import { ReactComponent as AdminIcon } from "../../../assets/icons/Settings.svg";
import { ReactComponent as UserIcon } from "../../../assets/icons/UserIcon.svg";
import { ReactComponent as PositionIcon } from "../../../assets/icons/PositionIcon.svg";
import { ReactComponent as RightsIcon } from "../../../assets/icons/RightsIcon.svg";
import { ReactComponent as LogoutIcon } from "../../../assets/icons/Logout.svg";
import { ReactComponent as MenuIcon } from "../../../assets/icons/SideBar.svg";

import { useAuth } from "../../../context/AuthContext";
import { PrivateElement } from "../../../components/PrivateElement/PrivateElement";

const adminMenu = [
    {
        label: "Пользователи",
        key: "ADMIN_USERS",
        icon: UserIcon,
        routes: [
            { label: "Добавить", path: ROUTES.ADMIN_USERS.create, exact: true },
            { label: "Обновить", path: ROUTES.ADMIN_USERS.update },
        ],
    },
    {
        label: "Должности",
        key: "ADMIN_POSITIONS",
        icon: PositionIcon,
        routes: [
            { label: "Добавить", path: ROUTES.ADMIN_POSITIONS.create, exact: true },
            { label: "Обновить", path: ROUTES.ADMIN_POSITIONS.update },
        ],
    },
    {
        label: "Права",
        key: "ADMIN_ROLES",
        icon: RightsIcon,
        routes: [
            { label: "Добавить", path: ROUTES.ADMIN_ROLES.create, exact: true },
            { label: "Обновить", path: ROUTES.ADMIN_ROLES.update },
        ],
    },
];

export default function Sidebar() {
    const { logout } = useAuth();
    const navigate = useNavigate();

    const [isAdminOpen, setIsAdminOpen] = useState(false);
    const [openSubmenus, setOpenSubmenus] = useState<{ [key: string]: boolean }>({});
    const [isMobileOpen, setIsMobileOpen] = useState(false);

    const handleLogout = async () => {
        try {
            await logout();
        } finally {
            navigate(ROUTES.LOGIN.path);
        }
    };

    const toggleSubmenu = (key: string) => {
        setOpenSubmenus(prev => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    return (
        <>
            {isMobileOpen && (
                <div
                    className={styles.overlay}
                    onClick={() => setIsMobileOpen(false)}
                />
            )}
            {!isMobileOpen && (
                <button
                    className={styles.floatingHamburger}
                    onClick={() => setIsMobileOpen(true)}
                    aria-label="Открыть меню"
                >
                    <MenuIcon />
                </button>
            )}
            <aside
                className={`${styles.sidebar} ${
                    isMobileOpen ? styles.mobileOpen : ""
                }`}
            >
                <div className={styles.logoWrapper}>
                    <div className={styles.logo}>
                        <div className={styles.logoLeft}>
                            <div className={styles.iconWrapper}>
                                <TruckIcon className={styles.icon} />
                            </div>

                            <div className={styles.logoText}>
                                Умный Вывоз
                                <span>Республика Бурятия</span>
                            </div>
                        </div>

                        {/* гамбургер СПРАВА от лого */}
                        <button
                            className={styles.mobileSidebarIcon}
                            onClick={() => setIsMobileOpen(prev => !prev)}
                            aria-label="Открыть меню"
                        >
                            <MenuIcon />
                        </button>
                    </div>
                </div>

                <ul className={styles.menu}>
                    <PrivateElement permissions={["CheckDashboard"]}>
                        <li>
                            <NavLink
                                to={ROUTES.DASHBOARD.path}
                                onClick={() => setIsMobileOpen(false)}
                                className={({ isActive }) =>
                                    `${styles.menuItem} ${isActive ? styles.active : ""}`
                                }
                            >
                                <DashboardIcon className={styles.icon} />
                                Дашборд
                            </NavLink>
                        </li>
                    </PrivateElement>

                    <PrivateElement permissions={["CheckMap"]}>
                        <li>
                            <NavLink
                                to={ROUTES.MAP.path}
                                onClick={() => setIsMobileOpen(false)}
                                className={({ isActive }) =>
                                    `${styles.menuItem} ${isActive ? styles.active : ""}`
                                }
                            >
                                <MapIcon className={styles.icon} />
                                Карта и мониторинг
                            </NavLink>
                        </li>
                    </PrivateElement>

                    <PrivateElement permissions={["CheckPlan"]}>
                        <li>
                            <NavLink
                                to={ROUTES.PLANNING.path}
                                onClick={() => setIsMobileOpen(false)}
                                className={({ isActive }) =>
                                    `${styles.menuItem} ${isActive ? styles.active : ""}`
                                }
                            >
                                <PlanningIcon className={styles.icon} />
                                Планирование
                            </NavLink>
                        </li>
                    </PrivateElement>

                    <PrivateElement permissions={["CheckGuide"]}>
                        <li>
                            <NavLink
                                to={ROUTES.HANDBOOK.path}
                                onClick={() => setIsMobileOpen(false)}
                                className={({ isActive }) =>
                                    `${styles.menuItem} ${isActive ? styles.active : ""}`
                                }
                            >
                                <HandbookIcon className={styles.icon} />
                                Справочник
                            </NavLink>
                        </li>
                    </PrivateElement>

                    <PrivateElement permissions={["CheckReport"]}>
                        <li>
                            <NavLink
                                to={ROUTES.REPORT.path}
                                onClick={() => setIsMobileOpen(false)}
                                className={({ isActive }) =>
                                    `${styles.menuItem} ${isActive ? styles.active : ""}`
                                }
                            >
                                <ReportIcon className={styles.icon} />
                                Отчетность
                            </NavLink>
                        </li>
                    </PrivateElement>

                    <PrivateElement permissions={["AdminRights"]}>
                        <li>
                            <div
                                className={styles.dropdownHeader}
                                onClick={() => setIsAdminOpen(prev => !prev)}
                            >
                                <AdminIcon className={styles.icon} />
                                Администрирование
                                <span className={styles.arrow}>
                                    {isAdminOpen ? "▲" : "▼"}
                                </span>
                            </div>

                            {isAdminOpen && (
                                <ul className={styles.submenu}>
                                    {adminMenu.map(menu => (
                                        <li key={menu.key}>
                                            <div
                                                className={`${styles.submenuHeader} ${
                                                    openSubmenus[menu.key]
                                                        ? styles.submenuOpen
                                                        : ""
                                                }`}
                                                onClick={() => toggleSubmenu(menu.key)}
                                            >
                                                <menu.icon className={styles.icon} />
                                                {menu.label}
                                                <span className={styles.arrow}>
                                                    {openSubmenus[menu.key] ? "▲" : "▼"}
                                                </span>
                                            </div>

                                            {openSubmenus[menu.key] && (
                                                <ul className={styles.innerSubmenu}>
                                                    {menu.routes.map(route => (
                                                        <li key={route.path}>
                                                            <NavLink
                                                                to={route.path}
                                                                end={route.exact || false}
                                                                onClick={() =>
                                                                    setIsMobileOpen(false)
                                                                }
                                                                className={({ isActive }) =>
                                                                    `${styles.submenuItem} ${
                                                                        isActive
                                                                            ? styles.activeSub
                                                                            : ""
                                                                    }`
                                                                }
                                                            >
                                                                {route.label}
                                                            </NavLink>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    </PrivateElement>
                </ul>

                <div className={styles.logoutWrapper}>
                    <button
                        className={styles.logoutButton}
                        onClick={handleLogout}
                    >
                        <LogoutIcon className={styles.icon} />
                        Выйти
                    </button>
                </div>
            </aside>
        </>
    );
}
